import { Link } from "react-router-dom";
import { Camera, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import { useT } from "../i18n";

const LOGO_URL = "https://www.perfinco.pt/wp-content/uploads/2022/07/perfinco-logo.png";

export default function Landing() {
  const { t } = useT();

  const features = [
    { icon: Camera, label: t("landing.feature_capture"), desc: t("landing.feature_capture_desc") },
    { icon: Sparkles, label: t("landing.feature_ai"), desc: t("landing.feature_ai_desc") },
    { icon: ShieldCheck, label: t("landing.feature_private"), desc: t("landing.feature_private_desc") },
  ];
  const steps = [
    { n: "01", title: t("landing.step1_title"), desc: t("landing.step1_desc") },
    { n: "02", title: t("landing.step2_title"), desc: t("landing.step2_desc") },
    { n: "03", title: t("landing.step3_title"), desc: t("landing.step3_desc") },
  ];

  return (
    <div className="paper-grain min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <section className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="label-eyebrow" data-testid="hero-eyebrow">{t("landing.eyebrow")}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-4 leading-[1.05]">
              {t("landing.title_1")} <em className="not-italic text-[color:var(--primary)]">{t("landing.title_brand")}</em>.
              <br />
              {t("landing.title_2")} <span className="underline decoration-[3px] decoration-[color:var(--primary)] underline-offset-4">{t("landing.title_emphasis")}</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[color:var(--ink-muted)] max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {t("landing.subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/login" className="btn-primary inline-flex items-center justify-center gap-2 text-base" data-testid="cta-register-btn">
                {t("landing.cta_invite")} <ArrowRight size={18} />
              </Link>
              <a href="mailto:geral@perfinco.pt?subject=Quero%20usar%20Perfinco%20Online" className="btn-secondary inline-flex items-center justify-center text-base" data-testid="cta-login-btn">
                {t("landing.cta_request")}
              </a>
            </div>

            <div className="mt-12 grid sm:grid-cols-3 gap-4">
              {features.map(({ icon: Icon, label, desc }) => (
                <div key={label} className="card-soft" data-testid={`feature-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon size={20} className="text-[color:var(--primary)]" />
                  <div className="mt-3 font-medium">{label}</div>
                  <div className="text-sm text-[color:var(--ink-muted)] mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[color:var(--primary)]/10 rounded-3xl rotate-2" />
              <div
                className="relative card-soft p-2 rotate-[-2deg] shadow-[0_30px_60px_-20px_rgba(40,20,80,0.35)]"
                style={{ background: "var(--surface)" }}
              >
                <img
                  alt="Exemplo de fatura"
                  className="rounded-xl w-full object-cover aspect-[4/5]"
                  src="https://static.prod-images.emergentagent.com/jobs/34389072-edc0-4805-8bf3-0b62347a61e2/images/dfa82a9635f6642aea78deab00119d4c44ebf06eb42205c96dddb14dc7be8d1d.png"
                />
                <div className="absolute bottom-6 left-6 right-6 card-soft p-4 bg-white/95">
                  <div className="flex items-center justify-between">
                    <span className="label-eyebrow">{t("landing.ai_detected")}</span>
                    <img src={LOGO_URL} alt="Perfinco" className="h-3.5 opacity-70" />
                  </div>
                  <div className="font-mono-data text-sm mt-2">
                    <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">NIF</span><span>507 153 626</span></div>
                    <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">Total</span><span>€ 142,30</span></div>
                    <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">Data</span><span>22 Jun 2026</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-24">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{t("landing.how_title")}</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="card-soft" data-testid={`step-${s.n}`}>
                <div className="label-eyebrow">{t("landing.step_label")} {s.n}</div>
                <div className="mt-3 font-display text-xl">{s.title}</div>
                <div className="text-sm text-[color:var(--ink-muted)] mt-2">{s.desc}</div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <footer className="border-t border-[color:var(--border)] mt-20 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[color:var(--ink-muted)]">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Perfinco" className="h-5 opacity-80" />
            <span>· Avenida Conde Valbom 6, 3.º · 1050-068 Lisboa</span>
          </div>
          <span>© 2026 Perfinco — {t("landing.footer_tagline")}</span>
        </div>
      </footer>
    </div>
  );
}
